/**
 * SequencingGroupBuilderPage — iter150AG.onboarding unified wizard.
 *
 * Per the workflow source-of-truth (user message 2026-02):
 *   1. Find compressors by serial.
 *   2. For each selected compressor, pick the CP device template +
 *      gateway model that controls how the device is provisioned in
 *      the Control Plane (admin-managed mapping resolves a smart
 *      default; the operator can override).
 *   3. Set group details (Pset, name).
 *   4. Provision (idempotent tenant → site → device push) and show
 *      the resulting MQTT topic paths `v1/<tenant>/<site>/<device>/{tx|rx}`
 *      so the maintenance technician can paste them into the on-site
 *      gateway. Topics are persisted on `seq_onboarded_devices` so any
 *      operator can re-fetch them later from the Live Dashboard.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, MagnifyingGlass, Lightning, Plus,
  CheckCircle, Warning, CircleNotch, Stack, Buildings, X,
  Copy, Cpu, BookOpenText,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqAvailableCompressors, seqCreateGroupFromCompressors,
  seqListDeviceTemplates, seqOnboardingPreview,
} from "@/lib/sequencingApi";


export default function SequencingGroupBuilderPage() {
  const navigate = useNavigate();
  const [step, setStep]               = useState(1);
  const [q, setQ]                     = useState("");
  const [inverterOnly, setInverterOnly] = useState(false);
  const [hideInGroup, setHideInGroup] = useState(true);

  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [err, setErr]                 = useState("");

  const [selected, setSelected]       = useState(/** @type {Set<string>} */(new Set()));
  // iter150AG fix — Cache of full compressor rows keyed by serial. This
  // outlives the live search filter so step 2 + the tenant banner can
  // see every checked compressor even after the operator types another
  // search query (and thus replaces `items`). Without this cache,
  // `selectedItems` was derived from the *currently visible* list only,
  // which silently dropped previously-checked compressors that no longer
  // matched the search.
  const [selectedCache, setSelectedCache] = useState(/** @type {Record<string,object>} */({}));
  const [lockedOwnerId, setLockedOwnerId] = useState(null);

  // Templates catalog + per-member overrides keyed by serial.
  const [templates, setTemplates]       = useState([]);
  const [memberMeta, setMemberMeta]     = useState(/** @type {Record<string,{plc_type?,inverter_type?,product_name?,suggested_template_id?,suggested_map_id?}>} */({}));
  const [overrides, setOverrides]       = useState(/** @type {Record<string,{device_template_external_id?:string,template_map_id?:string}>} */({}));

  const [groupName, setGroupName]         = useState("");
  const [psetInput, setPsetInput]         = useState("8.0");
  const [siteOverride, setSiteOverride]   = useState("");

  const [building, setBuilding]   = useState(false);
  const [buildResult, setBuildResult] = useState(null);

  async function search() {
    setLoading(true); setErr("");
    try {
      const params = { limit: 200 };
      if (q.trim())        params.q = q.trim();
      if (inverterOnly)    params.inverter_only = true;
      if (hideInGroup)     params.cp_status = "new";
      if (lockedOwnerId)   params.tenant_id = lockedOwnerId;
      const r = await seqAvailableCompressors(params);
      setItems(r.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { search(); /* eslint-disable-next-line */ }, [inverterOnly, hideInGroup, lockedOwnerId]);

  function toggleSelect(it) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(it.serial_number)) {
        next.delete(it.serial_number);
        if (next.size === 0) setLockedOwnerId(null);
        setSelectedCache(c => {
          const cp = { ...c };
          delete cp[it.serial_number];
          return cp;
        });
      } else {
        if (lockedOwnerId && it.owner_company_id !== lockedOwnerId) {
          toast.error("Mixed tenants aren't allowed in one group.");
          return prev;
        }
        next.add(it.serial_number);
        if (!lockedOwnerId) setLockedOwnerId(it.owner_company_id);
        setSelectedCache(c => ({ ...c, [it.serial_number]: it }));
      }
      return next;
    });
  }

  const selectedItems = useMemo(
    () => Array.from(selected)
              .map(s => selectedCache[s])
              .filter(Boolean),
    [selected, selectedCache]);

  const tenantSummary = selectedItems[0]
    ? { id: selectedItems[0].owner_company_id,
        name: selectedItems[0].owner_company_name,
        city: selectedItems[0].owner_city,
        state: selectedItems[0].owner_state }
    : null;

  // When advancing to step 2, fetch templates + per-serial onboarding
  // previews so the form can show smart suggestions.
  async function loadStep2() {
    try {
      const [tplResp, ...previews] = await Promise.all([
        seqListDeviceTemplates(),
        ...Array.from(selected).map(s => seqOnboardingPreview(s).catch(e => ({ _error: e, _serial: s }))),
      ]);
      setTemplates(tplResp.items || []);
      const next = {};
      for (const p of previews) {
        if (p?._error) {
          // Skip failed previews — operator can still pick manually.
          continue;
        }
        const sug = p.suggested_template || null;
        next[p.serial_number] = {
          plc_type:      p.compressor?.plc_type,
          inverter_type: p.compressor?.inverter_type,
          product_name:  p.compressor?.product_name,
          suggested_template_id: sug?.device_template_external_id,
          suggested_map_id:      sug?.id,
          suggested_name:        sug?.name,
          cp_state:              p.cp_state,
        };
      }
      setMemberMeta(next);
      // Pre-fill overrides with suggestions so the operator can see them.
      const nextOverrides = {};
      for (const [s, meta] of Object.entries(next)) {
        nextOverrides[s] = {
          device_template_external_id: meta.suggested_template_id || "",
          template_map_id:             meta.suggested_map_id || "",
        };
      }
      setOverrides(prev => ({ ...nextOverrides, ...prev }));
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load templates");
    }
  }

  function setOverride(serial, patch) {
    setOverrides(prev => ({
      ...prev,
      [serial]: { ...(prev[serial] || {}), ...patch },
    }));
  }

  async function runBuild() {
    setBuilding(true); setBuildResult(null);
    try {
      const member_overrides = Array.from(selected).map(s => {
        const o = overrides[s] || {};
        return {
          serial_number: s,
          device_template_external_id: o.device_template_external_id || undefined,
          template_map_id:             o.template_map_id || undefined,
        };
      });
      const payload = {
        name: groupName.trim(),
        serial_numbers: Array.from(selected),
        common_pset: parseFloat(psetInput) || 8.0,
        bandwidth_pct: 5.0,
        site_name_override: siteOverride.trim() || undefined,
        member_overrides,
      };
      const res = await seqCreateGroupFromCompressors(payload);
      setBuildResult(res);
      setStep(4);  // jump to "success + MQTT topics" panel
      toast.success("Group provisioned");
    } catch (e) {
      const det = e?.response?.data;
      if (det?.audit) {
        setBuildResult(det);
        setStep(4);
      }
      toast.error(det?.detail || det?.stage || e?.message || "Build failed");
    } finally { setBuilding(false); }
  }

  const canAdvance1 = selected.size > 0;
  const canAdvance2 = Array.from(selected).every(s => {
    const o = overrides[s] || {};
    return !!o.device_template_external_id;
  });
  const canBuild = canAdvance2 && groupName.trim().length > 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-group-builder">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing" data-testid="builder-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <Plus size={22} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            Onboard &amp; group compressors
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            Step {step} of 4 · pick → templates → group → MQTT topics
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-5 space-y-4 pb-24">
        {step === 1 && (
          <>
            <SearchBar
              q={q} setQ={setQ}
              inverterOnly={inverterOnly} setInverterOnly={setInverterOnly}
              hideInGroup={hideInGroup} setHideInGroup={setHideInGroup}
              onSearch={search}
              loading={loading} />
            {tenantSummary && (
              <TenantBanner tenant={tenantSummary}
                            onClear={() => { setSelected(new Set()); setSelectedCache({}); setLockedOwnerId(null); }} />
            )}
            <CompressorList
              items={items} selected={selected} onToggle={toggleSelect}
              loading={loading} err={err}
              lockedOwnerId={lockedOwnerId} />
            <Footer1 canNext={canAdvance1} selected={selected.size} onNext={loadStep2} />
          </>
        )}

        {step === 2 && (
          <TemplateStep
            selectedItems={selectedItems}
            templates={templates}
            memberMeta={memberMeta}
            overrides={overrides}
            setOverride={setOverride}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            canNext={canAdvance2} />
        )}

        {step === 3 && (
          <ReviewStep
            selectedItems={selectedItems}
            tenantSummary={tenantSummary}
            overrides={overrides}
            templates={templates}
            groupName={groupName} setGroupName={setGroupName}
            psetInput={psetInput} setPsetInput={setPsetInput}
            siteOverride={siteOverride} setSiteOverride={setSiteOverride}
            building={building}
            canBuild={canBuild}
            onBack={() => setStep(2)}
            onBuild={runBuild} />
        )}

        {step === 4 && (
          <ResultStep
            buildResult={buildResult}
            onBack={() => setStep(3)}
            onContinue={() => navigate(`/sequencing/groups/${buildResult.group_id}/commissioning`)} />
        )}
      </main>
    </div>
  );
}


// ─── Step 1 (compressor picker) ─────────────────────────────────────
function SearchBar({ q, setQ, inverterOnly, setInverterOnly,
                     hideInGroup, setHideInGroup, onSearch, loading }) {
  return (
    <section data-testid="builder-search-bar"
             className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
        <MagnifyingGlass size={14} className="text-slate-400" />
        <input
          data-testid="builder-search-input"
          type="text"
          value={q}
          placeholder="Search serial, customer…"
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === "Enter" && onSearch()}
          className="text-xs px-2 py-1 border border-slate-300 rounded-sm flex-1 min-w-[140px]" />
        <button data-testid="builder-search-go"
                onClick={onSearch}
                disabled={loading}
                className="text-[11px] px-2.5 py-1 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          {loading ? <CircleNotch size={11} className="animate-spin" /> : "Search"}
        </button>
      </div>
      <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
        <input type="checkbox" checked={inverterOnly}
                onChange={e => setInverterOnly(e.target.checked)}
                data-testid="builder-filter-inverter" />
        Inverter (VSD) only
      </label>
      <label className="inline-flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer">
        <input type="checkbox" checked={hideInGroup}
                onChange={e => setHideInGroup(e.target.checked)}
                data-testid="builder-filter-new" />
        Hide already-grouped
      </label>
    </section>
  );
}


function TenantBanner({ tenant, onClear }) {
  return (
    <section data-testid="builder-tenant-banner"
             className="bg-blue-50 border border-blue-200 rounded-sm p-2.5 flex items-center gap-2.5 text-xs">
      <Buildings size={14} weight="fill" className="text-blue-700" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">tenant locked</div>
        <div className="font-mono text-slate-800 truncate">
          {tenant.name || tenant.id}
          <span className="text-slate-400 ml-1">({tenant.id})</span>
        </div>
        {(tenant.city || tenant.state) && (
          <div className="text-[10px] text-slate-500">{[tenant.city, tenant.state].filter(Boolean).join(", ")}</div>
        )}
      </div>
      <button data-testid="builder-tenant-clear" onClick={onClear}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-blue-200 bg-white text-blue-700 hover:bg-blue-100">
        <X size={11} /> Clear
      </button>
    </section>
  );
}


function CompressorList({ items, selected, onToggle, loading, err, lockedOwnerId }) {
  if (loading && !items.length) return (
    <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="builder-list-loading">
      <CircleNotch size={12} className="animate-spin" /> Loading…
    </div>
  );
  if (err) return (
    <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm"
         data-testid="builder-list-error">{err}</div>
  );
  if (!items.length) return (
    <div className="bg-white border border-dashed border-slate-300 rounded-sm p-6 text-center text-slate-500 text-xs"
         data-testid="builder-list-empty">
      No compressors match. Try widening the search or unchecking the filters.
    </div>
  );
  return (
    <ul className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-[420px] overflow-y-auto"
        data-testid="builder-list">
      {items.map(it => {
        const isSel    = selected.has(it.serial_number);
        const disabled = lockedOwnerId && it.owner_company_id !== lockedOwnerId;
        return (
          <li key={it.serial_number}
              data-testid={`builder-row-${it.serial_number}`}
              onClick={() => !disabled && onToggle(it)}
              className={`px-3 py-2 flex items-center gap-3 cursor-pointer ${
                isSel ? "bg-blue-50"
                  : disabled ? "opacity-40 cursor-not-allowed"
                  : "hover:bg-slate-50"}`}>
            <input type="checkbox" readOnly checked={isSel} disabled={disabled}
                   className="w-3 h-3" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold font-mono text-slate-900">
                {it.display_serial || it.serial_number}
                <span className="text-[10px] text-slate-400 font-mono ml-2">{it.serial_number}</span>
              </div>
              <div className="text-[11px] text-slate-600 truncate">
                {it.owner_company_name || <span className="text-slate-400">— unowned —</span>}
                {it.product_name && <span className="text-slate-400 ml-2">{it.product_name}</span>}
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                {it.compressor_hours != null && <span>{it.compressor_hours.toLocaleString()} h</span>}
                {it.plc_type && <span>PLC {it.plc_type}</span>}
                {it.inverter && (
                  <span className="inline-flex items-center gap-1 text-blue-700">
                    <Lightning size={9} weight="fill" />
                    {it.inverter.type || it.inverter.make || "inverter"}
                  </span>
                )}
              </div>
            </div>
            {it.cp_status === "in_other_group" && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 border border-slate-300">
                in group
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}


function Footer1({ canNext, selected, onNext }) {
  return (
    <footer className="sticky bottom-0 bg-white border-t border-slate-200 p-3 flex items-center gap-2">
      <span className="text-[11px] text-slate-600" data-testid="builder-selected-count">
        <b>{selected}</b> selected
      </span>
      <button data-testid="builder-next-step1"
              disabled={!canNext} onClick={onNext}
              className="ml-auto text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1">
        Next: device types <ArrowRight size={12} weight="bold" />
      </button>
    </footer>
  );
}


// ─── Step 2 (per-compressor template selection) ─────────────────────
function TemplateStep({ selectedItems, templates, memberMeta, overrides,
                         setOverride, onBack, onNext, canNext }) {
  return (
    <div className="space-y-3" data-testid="builder-template-step">
      <section className="bg-white border border-slate-200 rounded-sm p-3">
        <div className="flex items-center gap-2">
          <Cpu size={14} className="text-blue-700" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Device type &amp; template per compressor</h2>
          <Link to="/sequencing/admin/device-templates"
                data-testid="builder-templates-link"
                className="ml-auto text-[11px] inline-flex items-center gap-1 text-blue-700 hover:underline">
            <BookOpenText size={11} /> Manage mappings
          </Link>
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          For each compressor, confirm or change the CP <b>device template</b>
          we'll send during provisioning. Smart suggestion comes from the
          admin-managed mapping table.
        </p>
      </section>

      {templates.length === 0 && (
        <div className="bg-amber-50 border border-amber-300 text-amber-800 text-xs p-3 rounded-sm"
             data-testid="builder-no-templates">
          <Warning size={12} weight="fill" className="inline -mt-0.5 mr-1" />
          No device-template mappings configured.{" "}
          <Link to="/sequencing/admin/device-templates" className="font-bold underline">
            Add at least one mapping
          </Link> before onboarding compressors.
        </div>
      )}

      <ul className="space-y-2" data-testid="builder-template-list">
        {selectedItems.map(it => {
          const meta  = memberMeta[it.serial_number] || {};
          const ovr   = overrides[it.serial_number] || {};
          const selectedMap = templates.find(t => t.id === ovr.template_map_id);
          return (
            <li key={it.serial_number}
                data-testid={`builder-template-row-${it.serial_number}`}
                className="bg-white border border-slate-200 rounded-sm p-3 space-y-2">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-xs font-bold font-mono text-slate-900">
                    {it.display_serial || it.serial_number}
                    <span className="text-[10px] text-slate-400 font-mono ml-2">{it.serial_number}</span>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {it.product_name || "—"}
                    {meta.plc_type && <span className="ml-2">PLC: <b>{meta.plc_type}</b></span>}
                    {meta.inverter_type && <span className="ml-2">Inv: <b>{meta.inverter_type}</b></span>}
                  </div>
                  {meta.cp_state && (
                    <div className="text-[10px] mt-1 flex flex-wrap gap-2">
                      <CpChip label="tenant" present={!!meta.cp_state.tenant} />
                      <CpChip label="site"   present={!!meta.cp_state.site} />
                      <CpChip label="device" present={!!meta.cp_state.device} />
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 min-w-[280px]">
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Device type</span>
                    <select
                      data-testid={`builder-template-select-${it.serial_number}`}
                      value={ovr.template_map_id || ""}
                      onChange={e => {
                        const id = e.target.value;
                        const t = templates.find(t => t.id === id);
                        setOverride(it.serial_number, {
                          template_map_id: id || undefined,
                          device_template_external_id: t?.device_template_external_id || "",
                        });
                      }}
                      className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm">
                      <option value="">— select —</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{meta.suggested_map_id === t.id ? " (suggested)" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                  {selectedMap && (
                    <div data-testid={`builder-template-info-${it.serial_number}`}
                          className="text-[10px] text-slate-500 leading-tight">
                      CP template id: <code className="font-mono text-slate-700">{selectedMap.device_template_external_id}</code>
                      {selectedMap.notes && <div className="text-slate-400 mt-0.5">{selectedMap.notes}</div>}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <footer className="sticky bottom-0 bg-white border-t border-slate-200 p-3 flex items-center gap-2">
        <button data-testid="builder-back-step2" onClick={onBack}
                className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
          ← Back
        </button>
        <button data-testid="builder-next-step2" disabled={!canNext} onClick={onNext}
                className="ml-auto text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1">
          Next: group details <ArrowRight size={12} weight="bold" />
        </button>
      </footer>
    </div>
  );
}


function CpChip({ label, present }) {
  return (
    <span data-testid={`builder-cp-chip-${label}-${present ? "present" : "missing"}`}
           className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
             present
               ? "bg-emerald-50 text-emerald-700 border-emerald-300"
               : "bg-slate-50 text-slate-500 border-slate-300"}`}>
      {present ? <CheckCircle size={9} weight="fill" className="inline -mt-0.5 mr-0.5" /> : <Plus size={9} className="inline -mt-0.5 mr-0.5" />}
      {label}
    </span>
  );
}


// ─── Step 3 (review + build) ─────────────────────────────────────────
function ReviewStep({ selectedItems, tenantSummary, overrides,
                       groupName, setGroupName,
                       psetInput, setPsetInput, siteOverride, setSiteOverride,
                       building, canBuild, onBack, onBuild }) {
  return (
    <div className="space-y-4" data-testid="builder-review-step">
      <section className="bg-white border border-slate-200 rounded-sm p-3 space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Group details</h2>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Group name *</span>
          <input data-testid="builder-group-name"
                  type="text" value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  placeholder="e.g. Plant A — Lead/Lag"
                  className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Common Pset (bar)</span>
            <input data-testid="builder-pset"
                    type="number" step="0.1" min="0.5" max="25"
                    value={psetInput}
                    onChange={e => setPsetInput(e.target.value)}
                    className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Site name override (optional)</span>
            <input data-testid="builder-site-override"
                    type="text" value={siteOverride}
                    onChange={e => setSiteOverride(e.target.value)}
                    placeholder={tenantSummary?.name ? `${tenantSummary.name} – HQ` : "auto"}
                    className="mt-1 w-full text-xs px-2 py-1.5 border border-slate-300 rounded-sm" />
          </label>
        </div>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-2"
               data-testid="builder-cp-preview">
        <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Control-Plane calls</h2>
        <ul className="text-[11px] space-y-1 font-mono">
          <li><span className="text-emerald-700">PUT</span> /api/external/tenants/<b>{tenantSummary?.id}</b></li>
          <li><span className="text-emerald-700">PUT</span> /api/external/sites/<b>site-{tenantSummary?.id}-hq</b></li>
          {selectedItems.map(it => (
            <li key={it.serial_number}>
              <span className="text-emerald-700">PUT</span> /api/external/devices/<b>{it.serial_number}</b>
              <span className="text-slate-400 ml-1">
                · tpl=<code>{(overrides[it.serial_number] || {}).device_template_external_id}</code>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <div className="flex items-center gap-2">
        <button data-testid="builder-back-to-step2" onClick={onBack} disabled={building}
                className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40">
          ← Back
        </button>
        <button data-testid="builder-build" onClick={onBuild}
                disabled={!canBuild || building}
                className="ml-auto text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 inline-flex items-center gap-1">
          {building ? <CircleNotch size={12} className="animate-spin" /> : <Stack size={12} weight="bold" />}
          Provision &amp; build
        </button>
      </div>
    </div>
  );
}


// ─── Step 4 (result + MQTT topics) ──────────────────────────────────
function ResultStep({ buildResult, onBack, onContinue }) {
  const ok = !!buildResult?.ok;
  const audit = buildResult?.audit || [];
  const onboarded = buildResult?.onboarded || {};
  const onboardedEntries = Object.entries(onboarded);

  return (
    <div className="space-y-4" data-testid="builder-result-step">
      <section className={`border rounded-sm p-4 ${
        ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}
               data-testid="builder-result">
        <div className="flex items-center gap-2 text-sm font-bold">
          {ok
            ? <><CheckCircle size={16} weight="fill" className="text-emerald-700" /> Group provisioned</>
            : <><Warning size={16} weight="fill" className="text-rose-700" /> Build failed{buildResult?.stage ? ` at ${buildResult.stage}` : ""}</>}
        </div>
        <ul className="mt-2 text-[11px] font-mono space-y-1">
          {audit.map((a, idx) => (
            <li key={idx} className="flex items-start gap-2">
              {a.ok
                ? <CheckCircle size={12} weight="fill" className="text-emerald-700 mt-0.5" />
                : <Warning size={12} weight="fill" className="text-rose-700 mt-0.5" />}
              <span className="font-bold uppercase text-[10px] text-slate-600 w-12">{a.step}</span>
              <span className="text-slate-700">{a.external_id || ""}</span>
              {a.was_existing && <span className="text-[10px] text-slate-500">existing</span>}
              {a.via && <span className="text-[10px] text-slate-500">via {a.via}</span>}
              {a.error && <span className="text-rose-700">{a.error}</span>}
            </li>
          ))}
        </ul>
      </section>

      {ok && onboardedEntries.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-sm p-4 space-y-3"
                 data-testid="builder-mqtt-panel">
          <div className="flex items-center gap-2">
            <Lightning size={16} weight="fill" className="text-amber-600" />
            <h2 className="text-sm font-bold font-['Chivo'] text-slate-900">MQTT topic paths</h2>
          </div>
          <p className="text-[11px] text-slate-500">
            Paste the matching <code>tx</code> + <code>rx</code> topic into the on-site MQTT
            gateway's subscription config. Format is
            <code className="ml-1">v1/&lt;tenant&gt;/&lt;site&gt;/&lt;device&gt;/{"{tx|rx}"}</code>.
            The CP will deliver server→device commands on <code>tx</code> and
            ingest device→server telemetry on <code>rx</code>.
          </p>
          {onboardedEntries.map(([serial, row]) => (
            <div key={serial} data-testid={`builder-mqtt-row-${serial}`}
                  className="border border-slate-200 rounded-sm p-2 bg-slate-50">
              <div className="text-xs font-bold font-mono text-slate-800">{serial}</div>
              <div className="text-[10px] text-slate-500 mb-1">
                template <code>{row.device_template_external_id}</code>
                {" "}· cp_device_id <code>{row.cp_device_id}</code>
              </div>
              <TopicLine label="tx" value={row.tx_topic} testid={`builder-mqtt-tx-${serial}`} />
              <TopicLine label="rx" value={row.rx_topic} testid={`builder-mqtt-rx-${serial}`} />
            </div>
          ))}
        </section>
      )}

      <footer className="flex items-center gap-2">
        <button data-testid="builder-result-back" onClick={onBack}
                className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
          ← Back to review
        </button>
        {ok && buildResult?.group_id && (
          <button data-testid="builder-result-continue" onClick={onContinue}
                  className="ml-auto text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1">
            Continue to commissioning <ArrowRight size={12} weight="bold" />
          </button>
        )}
      </footer>
    </div>
  );
}


function TopicLine({ label, value, testid }) {
  async function copy() {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} topic copied`);
    } catch {
      toast.error("Clipboard unavailable — long-press to copy manually");
    }
  }
  return (
    <div className="flex items-center gap-2 text-[11px] py-0.5">
      <span className="font-bold uppercase text-[10px] text-slate-600 w-5">{label}</span>
      <code data-testid={testid}
             className="flex-1 font-mono bg-white border border-slate-200 rounded-sm px-2 py-1 truncate">
        {value || "—"}
      </code>
      <button data-testid={`${testid}-copy`} onClick={copy}
              className="text-[10px] inline-flex items-center gap-1 px-1.5 py-1 rounded-sm border border-slate-200 bg-white text-slate-600 hover:text-blue-700 hover:bg-blue-50">
        <Copy size={10} /> copy
      </button>
    </div>
  );
}
